import type { ReactNode } from 'react';
import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { iconKey, iconSpinnerThird } from '@/lib/icons';

/** A link to where the key lives, in the provider's own app. */
export function KeyHelpLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className='text-[#3E8BFF] hover:underline'
    >
      {children}
    </a>
  );
}

/**
 * The one look for connecting an integration with an API key: a key icon,
 * where to find the key, the key field, and Cancel / Connect. `children`
 * holds any extra field (Trellus' calling list). `cancelWhileBusy` keeps
 * Cancel live while the key is checked, for callers that drop a late answer
 * once the dialog closes.
 */
export function ApiKeyConnectDialog({
  name,
  open,
  onOpenChange,
  help,
  value,
  onValueChange,
  onSubmit,
  busy = false,
  error,
  canSubmit = true,
  cancelWhileBusy = false,
  inputLabel = `${name} API key`,
  placeholder = 'Paste your API key...',
  children,
}: {
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  help: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: () => void;
  busy?: boolean;
  error?: string | null;
  canSubmit?: boolean;
  cancelWhileBusy?: boolean;
  inputLabel?: string;
  placeholder?: string;
  children?: ReactNode;
}) {
  const ready = !!value.trim() && canSubmit && !busy;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='rounded-lg border border-black/[0.06] bg-white sm:max-w-md dark:border-white/[0.06] dark:bg-[#252525]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AppIcon
              icon={iconKey}
              className='text-[#3E8BFF]'
              style={{ fontSize: 16 }}
            />
            Connect {name}
          </DialogTitle>
          <DialogDescription>{help}</DialogDescription>
        </DialogHeader>
        <form
          className='space-y-3 py-2'
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) onSubmit();
          }}
        >
          <Input
            type='password'
            aria-label={inputLabel}
            aria-invalid={!!error}
            autoComplete='new-password'
            data-1p-ignore
            data-lpignore='true'
            placeholder={placeholder}
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            disabled={busy}
            autoFocus
          />
          {children}
          {error && (
            <p role='alert' className='text-xs text-destructive'>
              {error}
            </p>
          )}
          {/* Enter in any field submits. */}
          <button type='submit' hidden />
        </form>
        <DialogFooter>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onOpenChange(false)}
            disabled={busy && !cancelWhileBusy}
          >
            Cancel
          </Button>
          <Button size='sm' onClick={onSubmit} disabled={!ready}>
            {busy ? (
              <>
                <AppIcon
                  icon={iconSpinnerThird}
                  className='mr-1.5 animate-spin'
                  style={{ fontSize: 14 }}
                />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
